import { AuthMethod, IDURA_AUTH_METHODS } from 'models/user';

/**
 * DD-3513 — which of the Idura e-IDs this build lets a user connect.
 *
 * Every e-ID needs its own production agreement with Idura, signed on different
 * timelines, and REACT_APP_ENABLED_EID_METHODS lets a build offer only the ones that
 * work in its environment. Comma-separated AuthMethod names, e.g. `BANKID_SE` or
 * `BANKID_SE,MITID_DK,FTN_FI`. Must match the auth-frontend build, or a user could
 * connect an e-ID the login page does not offer.
 *
 * Unset means all of them, so a forgotten variable never hides Swedish BankID. An
 * explicitly empty value means none. Unknown names are ignored and the canonical order
 * is kept.
 *
 * Only the *connect* cards are gated. A user who already holds a binding for a method
 * that is switched off still sees it on their account page and can still disconnect it;
 * that is IDURA_AUTH_METHODS, which stays the full list.
 */
export const parseEnabledEidMethods = (raw: string | undefined): AuthMethod[] => {
    if (raw === undefined) {
        return [...IDURA_AUTH_METHODS];
    }
    const wanted = new Set(
        raw
            .split(',')
            .map((name) => name.trim().toUpperCase())
            .filter((name) => name.length > 0),
    );
    return IDURA_AUTH_METHODS.filter((method) => wanted.has(method));
};

export const ENABLED_EID_METHODS: readonly AuthMethod[] = parseEnabledEidMethods(
    process.env.REACT_APP_ENABLED_EID_METHODS,
);

export const isEidMethodEnabled = (method: AuthMethod | null | undefined): boolean =>
    !!method && ENABLED_EID_METHODS.includes(method);
